import {IConditionalRuleSet, IFormElement, IListViewContentElement, TImageUploadFrameShapes} from "~/interfaces/formSubmissionEditInterfaces"
import {TimeFormat} from "~/models/timeFormat"
import {ValidationReferenceType} from "~/enums/validationReferenceType"
import {DatetimeMode} from "~/models/datetimeMode"
import {FormElementType} from "~/enums/formElementType"
import {DatetimeType} from "~/models/datetimeType"
import {VerticalAlignment} from "~/enums/verticalAlignment"
import {ArrayHelper} from "~/helpers/arrayHelper"
import {ICharacterCount} from "~/interfaces/characterCountInterfaces"
import {ValidationType} from "~/enums/validationType"
import {LookupFieldType} from "~/enums/lookupFieldType"
import {CurrencyType} from "../../../formability/form_builder/models/currencyType"
import Revive from "./revive"
import {IFormElementRevive} from "~/interfaces/reviveInterfaces"
import {DefaultOptionType} from "../../../formability/form_builder/models/defaultOptionType"

export default class FormElementRevive extends Revive<IFormElementRevive> implements IFormElementRevive {
  add_repeatable_message: string
  allow_image_min_max: boolean
  character_count: ICharacterCount
  character_count_conditional_rule_sets: Array<IConditionalRuleSet<this['character_count']>>
  check_by_default: boolean
  client_id: string
  column_count: number
  cropper_data: { frame_height: number; frame_width: number; aspect_ratio: number; frame_shape: TImageUploadFrameShapes }
  currency_type: CurrencyType
  datetime_mode: DatetimeMode
  decimal_places: number
  default_datetime: string;
  default_datetime_formula?: string;
  default_datetime_type: DatetimeType;
  default_option: string
  default_option_id: string
  default_option_type: DefaultOptionType
  default_show: boolean
  default_show_conditional_rule_sets: Array<IConditionalRuleSet<this['default_show']>>
  default_value: string
  default_value_conditional_rule_sets: Array<IConditionalRuleSet<this['default_value']>>
  default_value_formula: string
  display_label: string
  element_type: FormElementType
  file_restriction_friendly_text: string
  font_size_class: string
  form_elements: Array<IFormElement>
  format_message: string
  format_numeric_with_commas: boolean
  format_pattern: string
  format_type: ValidationType
  frame_height: number
  frame_shape: number
  frame_width: number
  has_display_label: boolean
  help_text: string
  id: string
  image_height_max: number
  image_height_min: number
  image_width_max: number
  image_width_min: number
  individual_max_file_size_mb: number
  is_file_type_restricted: boolean
  is_masked: boolean
  is_multiple: boolean
  is_readonly: boolean
  is_readonly_conditional_rule_sets: Array<IConditionalRuleSet<this["is_readonly"]>>
  is_repeatable: boolean
  is_required: boolean
  is_required_conditional_rule_sets: Array<IConditionalRuleSet<this['is_required']>>
  is_wysiwyg_formattable: boolean
  label_title: string
  label_title_conditional_rule_sets: Array<IConditionalRuleSet<this['label_title']>>
  label_title_formula: string
  layout_config: Array<number>
  list_view: { content_elements: Array<IListViewContentElement & { content_elements: Array<IListViewContentElement> }> }
  list_view_id: string
  lookup_type: LookupFieldType
  max_datetime: string
  max_datetime_formula?: string
  max_datetime_type: DatetimeType
  max_repeatable_instances: number
  max_value: number
  min_datetime: string
  min_datetime_formula?: string
  min_datetime_type: DatetimeType
  min_repeatable_instances: number
  min_value: number
  object_model_instance_selection_limit: number | null
  object_model_property_names: Array<string>
  placeholder_text: string
  profile_config_field_id: string
  published_list_view?: { content_elements: Array<IListViewContentElement & { content_elements: Array<IListViewContentElement> }> }
  reference_data_ids: Array<string>
  reference_name: string
  required_message: string
  restricted_file_types: string[] | null
  sort_order: number
  time_format: TimeFormat
  total_max_file_size_mb: number
  validation_reference_type: ValidationReferenceType
  validation_rule: string
  validation_rule_conditional_rule_sets: Array<IConditionalRuleSet>
  vertical_alignment: VerticalAlignment
  wysiwyg_formattable_text: string

  get sortedCharacterCountConditionalRuleSets(): IFormElementRevive['sortedCharacterCountConditionalRuleSets'] {
    return this.cached('sortedCharacterCountConditionalRuleSets', () => {
      if (!this.character_count_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.character_count_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedDefaultValueConditionalRuleSets(): IFormElementRevive['sortedDefaultValueConditionalRuleSets'] {
    return this.cached('sortedDefaultValueConditionalRuleSets', () => {
      if (!this.default_value_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.default_value_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedDefaultShowConditionalRuleSets(): IFormElementRevive['sortedDefaultShowConditionalRuleSets'] {
    return this.cached('sortedDefaultShowConditionalRuleSets', () => {
      if (!this.default_show_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.default_show_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedIsReadonlyConditionalRuleSets(): IFormElementRevive['sortedIsReadonlyConditionalRuleSets'] {
    return this.cached('sortedIsReadonlyConditionalRuleSets', () => {
      if (!this.is_readonly_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.is_readonly_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedIsRequiredConditionalRuleSets(): IFormElementRevive['sortedIsRequiredConditionalRuleSets'] {
    return this.cached('sortedIsRequiredConditionalRuleSets', () => {
      if (!this.is_required_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.is_required_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedLabelTitleConditionalRuleSets(): IFormElementRevive['sortedLabelTitleConditionalRuleSets'] {
    return this.cached('sortedLabelTitleConditionalRuleSets', () => {
      if (!this.label_title_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.label_title_conditional_rule_sets.slice(), 'sort_order')
    })
  }

  get sortedValidationRuleConditionalRuleSets(): IFormElementRevive['sortedValidationRuleConditionalRuleSets'] {
    return this.cached('sortedValidationRuleConditionalRuleSets', () => {
      if (!this.validation_rule_conditional_rule_sets) return []
      return ArrayHelper.sortByNumber(this.validation_rule_conditional_rule_sets.slice(), 'sort_order')
    })
  }
}